/**
 * @fileoverview Badge Evaluation Utility
 *
 * Determines which badges should be displayed for an event based on
 * current context and event properties. Implements priority-based
 * badge selection logic.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */
import { getBadgeConfig } from '@loci/types';
import { haversine } from '@loci/validation';
/**
 * Default configuration for badge evaluation
 */
const DEFAULT_CONFIG = {
    nearThreshold: 5000, // 5km in meters
    newThreshold: 7, // 7 days
    almostFullThreshold: 0.1, // 10% remaining capacity
};
/**
 * Check if event is currently live
 */
function isEventLive(context) {
    const now = context.now.getTime();
    const start = context.eventStartTime.getTime();
    const end = context.eventEndTime?.getTime();
    if (!end) {
        // If no end time, consider live for 4 hours after start
        return now >= start && now <= start + 4 * 60 * 60 * 1000;
    }
    return now >= start && now <= end;
}
const normalizeCoordinates = (coords) => {
    if (!coords) {
        return null;
    }
    const lat = typeof coords.lat === 'number' ? coords.lat : coords.latitude;
    const lng = typeof coords.lng === 'number' ? coords.lng : coords.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return null;
    }
    return { lat, lng };
};
/**
 * Check if event is near user
 */
function isEventNear(context) {
    const userCoords = normalizeCoordinates(context.userLocation);
    const eventCoords = normalizeCoordinates(context.eventLocation);
    if (!userCoords || !eventCoords) {
        return false;
    }
    const threshold = context.nearThreshold || DEFAULT_CONFIG.nearThreshold;
    const distance = haversine({
        lat1: userCoords.lat,
        lon1: userCoords.lng,
        lat2: eventCoords.lat,
        lon2: eventCoords.lng,
    });
    return distance <= threshold;
}
/**
 * Check if event is new
 */
function isEventNew(context) {
    if (!context.createdAt) {
        return false;
    }
    const threshold = context.newThreshold || DEFAULT_CONFIG.newThreshold;
    const daysSinceCreation = (context.now.getTime() - context.createdAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceCreation <= threshold;
}
/**
 * Check if event is almost full
 */
function isEventAlmostFull(context) {
    if (context.availableTickets === undefined ||
        context.totalCapacity === undefined ||
        context.totalCapacity === 0) {
        return false;
    }
    const remainingRatio = context.availableTickets / context.totalCapacity;
    return remainingRatio <= DEFAULT_CONFIG.almostFullThreshold && remainingRatio > 0;
}
/**
 * Evaluate which badges should be displayed for an event
 *
 * @param context - Event and user context
 * @returns Badge evaluation result with primary, secondary, and all applicable badges
 */
export function evaluateBadges(context) {
    const applicableBadges = [];
    // Check each badge condition
    if (context.isCanceled) {
        applicableBadges.push('canceled');
    }
    if (isEventLive(context)) {
        applicableBadges.push('live-now');
    }
    if (context.isSoldOut) {
        applicableBadges.push('sold-out');
    }
    if (isEventAlmostFull(context) && !context.isSoldOut) {
        applicableBadges.push('almost-full');
    }
    if (context.isRegistered) {
        applicableBadges.push('registered');
    }
    if (context.isFeatured) {
        applicableBadges.push('featured');
    }
    if (isEventNear(context) && !context.isOnline) {
        applicableBadges.push('near-you');
    }
    if (isEventNew(context)) {
        applicableBadges.push('new');
    }
    if (context.isOnline) {
        applicableBadges.push('online');
    }
    if (context.isRecurring) {
        applicableBadges.push('recurring');
    }
    // Sort badges by priority (highest first)
    const sortedBadges = sortBadgesByPriority(applicableBadges);
    // Determine primary and secondary badges
    const primary = sortedBadges[0] || null;
    const secondary = sortedBadges.slice(1, 3); // Up to 2 secondary badges
    return {
        primary,
        secondary,
        all: sortedBadges,
        context,
    };
}
/**
 * Get badge priority
 */
export function getBadgePriority(type) {
    return getBadgeConfig(type)?.priority ?? 0;
}
/**
 * Sort badges by priority
 */
export function sortBadgesByPriority(badges) {
    return [...badges].sort((a, b) => {
        const priorityA = getBadgeConfig(a)?.priority ?? 0;
        const priorityB = getBadgeConfig(b)?.priority ?? 0;
        if (priorityB !== priorityA) {
            return priorityB - priorityA;
        }
        return a.localeCompare(b); // Stable sort for tie-breaking
    });
}
/**
 * Filter badges based on display rules
 * For example, don't show 'almost-full' if 'sold-out' is present
 */
export function filterBadges(badges) {
    const filtered = new Set(badges);
    // If sold out, remove almost-full
    if (filtered.has('sold-out')) {
        filtered.delete('almost-full');
    }
    // If canceled, show only canceled badge
    if (filtered.has('canceled')) {
        return ['canceled'];
    }
    // If live-now, remove 'new' badge
    if (filtered.has('live-now')) {
        filtered.delete('new');
    }
    return Array.from(filtered);
}
/**
 * Get recommended badges for display (with filtering and limiting)
 */
export function getRecommendedBadges(context, maxBadges = 3) {
    const evaluation = evaluateBadges(context);
    const filtered = filterBadges(evaluation.all);
    const sorted = sortBadgesByPriority(filtered);
    return sorted.slice(0, maxBadges);
}
